/**
 * Artifact Card Component - Claude Style
 *
 * Inline card that appears in messages when artifacts are generated.
 * Shows: Code icon | Title + Subtitle | Open button
 */

import * as React from 'react'
import { Code2, TableProperties, FileSpreadsheet, BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface ArtifactCardData {
  id: string
  type: 'chart' | 'table' | 'export' | 'code'
  title: string
  subtitle?: string
}

interface ArtifactCardProps {
  artifact: ArtifactCardData
  onClick?: () => void
  className?: string
}

// Artifact type configuration
const artifactConfig: Record<string, {
  icon: React.ElementType
  label: string
  fileType: string
}> = {
  chart: {
    icon: BarChart3,
    label: 'Chart',
    fileType: 'JSX'
  },
  table: {
    icon: TableProperties,
    label: 'Table',
    fileType: 'Data'
  },
  export: {
    icon: FileSpreadsheet,
    label: 'Export',
    fileType: 'CSV'
  },
  code: {
    icon: Code2,
    label: 'Code',
    fileType: 'JSX'
  },
}

const defaultConfig = {
  icon: Code2,
  label: 'Item',
  fileType: 'File'
}

export function ArtifactCard({
  artifact,
  onClick,
  className,
}: ArtifactCardProps) {
  const config = artifactConfig[artifact.type] || defaultConfig
  const Icon = config.icon

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl',
        'border border-border bg-card',
        'max-w-md',
        className
      )}
    >
      {/* Code Icon Box - bordered container with code brackets */}
      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted/30">
        <span className="text-muted-foreground font-mono text-lg">&lt;/&gt;</span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{artifact.title}</div>
        <div className="text-xs text-muted-foreground">
          {artifact.subtitle || `${config.label} · ${config.fileType}`}
        </div>
      </div>

      {/* Open Button */}
      <Button
        variant="outline"
        size="sm"
        className="rounded-lg px-4 font-medium"
        onClick={(e) => {
          e.stopPropagation()
          onClick?.()
        }}
      >
        Open
      </Button>
    </div>
  )
}

/**
 * Multiple artifacts display
 */
interface ArtifactCardsProps {
  artifacts: ArtifactCardData[]
  onClickArtifact?: (artifact: ArtifactCardData) => void
  className?: string
}

export function ArtifactCards({
  artifacts,
  onClickArtifact,
  className,
}: ArtifactCardsProps) {
  if (artifacts.length === 0) {
    return null
  }

  return (
    <div className={cn('space-y-2 mt-3', className)}>
      {artifacts.map((artifact) => (
        <ArtifactCard
          key={artifact.id}
          artifact={artifact}
          onClick={() => onClickArtifact?.(artifact)}
        />
      ))}
    </div>
  )
}
